import { authFetch, isAuthenticated } from './auth';

const API_URL = window.location.hostname === 'localhost' ? 'http://localhost:8100/api' : '/api';

export const SCAN_MAX_SIDE = 1600;
export const MAX_LABELS_PER_PRODUCT = 4;

// Scales a photo down to at most `maxSide` px (JPEG), honouring the EXIF orientation.
// Returns a File; if the browser cannot decode the image the original file is returned and the server decides.
export async function downscaleImage(file, maxSide = SCAN_MAX_SIDE) {
  try {
    let bitmap;
    if (window.createImageBitmap) {
      try { bitmap = await createImageBitmap(file, { imageOrientation: 'from-image' }); }
      catch { bitmap = await createImageBitmap(file); }
    } else {
      bitmap = await new Promise((resolve, reject) => {
        const img = new Image();
        img.onload = () => resolve(img);
        img.onerror = reject;
        img.src = URL.createObjectURL(file);
      });
    }
    const width = bitmap.width;
    const height = bitmap.height;
    const scale = Math.min(1, maxSide / Math.max(width, height));
    const canvas = document.createElement('canvas');
    canvas.width = Math.max(1, Math.round(width * scale));
    canvas.height = Math.max(1, Math.round(height * scale));
    const context = canvas.getContext('2d');
    context.fillStyle = '#fff';
    context.fillRect(0, 0, canvas.width, canvas.height);
    context.drawImage(bitmap, 0, 0, canvas.width, canvas.height);
    if (bitmap.close) bitmap.close();
    const blob = await new Promise((resolve) => canvas.toBlob(resolve, 'image/jpeg', 0.85));
    if (!blob) return file;
    return new File([blob], 'label.jpg', { type: 'image/jpeg' });
  } catch {
    return file;
  }
}

// POST /api/labels/scan. Resolves with the API answer; rejects with { code, detail } (detail is localized by the API).
export async function scanLabel(file, kind) {
  const body = new FormData();
  body.append('file', file);
  body.append('kind', kind);
  let res;
  try {
    res = await authFetch(`${API_URL}/labels/scan`, { method: 'POST', body });
  } catch {
    throw { code: 'network', detail: null }; // eslint-disable-line no-throw-literal
  }
  if (!res) throw { code: 'network', detail: null }; // eslint-disable-line no-throw-literal
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw { code: data.code || null, detail: data.detail || null }; // eslint-disable-line no-throw-literal
  return data;
}

const isEmpty = (value) => value === '' || value === null || value === undefined;

// Copies scanned values into `current` (an object of form values) only where the user has not typed anything yet.
// With { force: true } the scanned values overwrite (used when the user taps a suggestion to assign it).
export function mergeScanValues(current, values, { force = false } = {}) {
  const merged = { ...current };
  Object.entries(values || {}).forEach(([key, value]) => {
    if (isEmpty(value)) return;
    if (force || isEmpty(current[key])) merged[key] = value;
  });
  return merged;
}

// Wine forms use `wine_name` where the scan answers `name`; fields the form does not have are dropped.
export function applyScanToWineForm(form, values, options) {
  const mapped = {};
  Object.entries(values || {}).forEach(([key, value]) => {
    const target = key === 'name' ? 'wine_name' : key;
    if (target in form) mapped[target] = value;
  });
  return mergeScanValues(form, mapped, options);
}

// Adds the scanned photo to the wine catalog as a label photo (registered users only). Never throws:
// returns true when uploaded, false when skipped (anonymous, 4 labels already, any error).
export async function uploadWineLabel(wine, file) {
  if (!file || !isAuthenticated() || !wine || !wine.name) return false;
  try {
    const upsert = await authFetch(`${API_URL}/wines`, {
      method: 'POST',
      body: JSON.stringify({ name: wine.name, vintage: wine.vintage || null, producer: wine.producer || null }),
    });
    if (!upsert || !upsert.ok) return false;
    const catalogWine = await upsert.json();
    const listing = await authFetch(`${API_URL}/wines/${catalogWine.id}/attachments`);
    if (!listing || !listing.ok) return false;
    if ((await listing.json()).length >= MAX_LABELS_PER_PRODUCT) return false;
    const body = new FormData();
    body.append('file', file);
    const res = await authFetch(`${API_URL}/wines/${catalogWine.id}/attachments`, { method: 'POST', body });
    return !!(res && res.ok);
  } catch {
    return false;
  }
}
